import * as Yup from "yup";
import { FC, useMemo, useState } from "react";
import { FormTextInput } from "../../components/FormFields";
import { PrimaryButton } from "../../components/Buttons";
import WarningModal from "../../components/WarningModal";
import { XCircleIcon } from "@heroicons/react/24/outline";
import { processBarcode } from "../../utils";
import { alertService } from "../../services";
import { useAppDispatch, useAppInventory, useAppSelector } from "../../store/hooks";
import {
  InputType,
  ManifestDetailSingleInterface,
  ManifestIncidence,
  ManifestType,
} from "../../interfaces/ManifestInterface";
import {
  addPendingDetails,
  addProcessedDetails,
  removePendingDetails,
} from "../../store/slices/inventory";
import { Formik, FormikHelpers } from "formik";
import {
  getOfficeShipment,
} from "../../services/manifestServices";
import { InventoryDetailSingleInterface, StatusInventory } from "../../interfaces/InventoryInterface";
import { createInventoryDetail, createObjectInventoryDetail, validationInventoryDetail } from "../../services/inventoryServices";

interface ShipmentPieceFormValues {
  shipmentNumber: string;
  pieceNumber: number;
  random: number;
}
const INITIAL_VALUES: ShipmentPieceFormValues = {
  shipmentNumber: "",
  pieceNumber: 0,
  random: 0,
};

interface InventoryDetailFormProps {
  addDetailsBeingValidated: (details: InventoryDetailSingleInterface[]) => void;
  removeDetailsBeingValidated: (
    details: InventoryDetailSingleInterface[]
  ) => void;
}
const InventoryDetailForm: FC<InventoryDetailFormProps> = ({
  addDetailsBeingValidated,
  removeDetailsBeingValidated,
}) => {
  const inventory = useAppInventory();
  const user = useAppSelector((state) => state.user);
  const businessServices = useAppSelector(
    (state) => state.inmutable.businessServices
  );
  const inventoryHeader = useAppSelector(
    (state) => state.inventory.inventoryHeader
  );
  const pendingDetails = useAppSelector(
    (state) => state.inventory.pendingDetails
  );
  const processedDetails = useAppSelector(
    (state) => state.inventory.processedDetails
  );

  const [barCode, setBarCode] = useState("");
  const [initialValues, setInitialValues] = useState(INITIAL_VALUES);
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [errorMessageModal, setErrorMessageModal] = useState<string | JSX.Element>("");

  const validationSchema = Yup.object().shape({
    shipmentNumber: Yup.string().required("El número de guía es requerido"),
    pieceNumber: Yup.number().required("El número de pieza es requerido"),
  });

  const detailList = useMemo(() => {
    return Object.keys(processedDetails)
      .map((id) => processedDetails[id])
      .concat(Object.keys(pendingDetails).map((id) => pendingDetails[id]));
  }, [processedDetails, pendingDetails]);

  const addDetail = async (detail: ManifestDetailSingleInterface) => {
    const userLogin = user.user?.userLogin;
    const buCode = user.businessUnit?.code;
    const inventoryHeaderID = inventoryHeader?.inventoryHeaderID;

    if (!userLogin) {
      alertService.error(
        "No hay un nombre de usuario asociado a la sesión actual"
      );
      return;
    }
    if (!buCode) {
      alertService.error("No hay una tienda asociada a la sesión del usuario");
      return;
    }
    if (!inventoryHeaderID) {
      alertService.error(
        "Se necesita crear el inventario antes de agregar piezas"
      );
      return;
    }
    if (
      detailList.some(
        (d) =>
          d.shipmentNumber === detail.shipmentNumber &&
          d.pieceNumber === detail.pieceNumber
      )
    ) {
      alertService.warn(
        `Ya se agregó la pieza ${detail.pieceNumber} de la guía ${detail.shipmentNumber}`,
        "",
        { autoClose: false }
      );
      return;
    }

    inventory(addPendingDetails([detail]));
    const validations = await validationInventoryDetail(
      [detail],
      buCode
    );

    if (
      validations.didError ||
      !validations.model ||
      validations.model.length === 0
    ) {
      alertService.error(
        "Hubo un error validando la pieza",
        validations.errorMessage
      );
      return;
    }

    const validatedDetail = {
      ...detail,
      incidence: validations.model[0].incidence,
      errors: validations.model[0].errors,
    };

    const keyword = "tiempo maximo de permanencia"; 
    const incidenceSegments = String(validatedDetail.incidence || "").split("|").map(msg => msg.trim());
    const errorSegments = String(validatedDetail.errors || "").split("|").map(msg => msg.trim());
    const allMessages = [...incidenceSegments, ...errorSegments];
    const matchMessage = allMessages.find(msg => 
      msg.toLowerCase().includes(keyword.toLowerCase())
    );

    if (matchMessage) {
      setErrorMessageModal(matchMessage);
      setOpenErrorModal(true);
    }

    // if (validatedDetail.incidence !== ManifestIncidence.NO_ERROR) {
    //   inventory(removePendingDetails([validatedDetail]));
    //   inventory(addProcessedDetails([validatedDetail]));
    //   return;
    // }

    const manifestDetail = createObjectInventoryDetail(
      [validatedDetail],
      inventoryHeaderID,
      userLogin,
      buCode,
      false
    );
    const insertedDetail = await createInventoryDetail(manifestDetail);

    if (insertedDetail.didError || !insertedDetail.model) {
      alertService.error(
        "Hubo un error insertando la pieza al inventario",
        insertedDetail.errorMessage
      );
      const invalidDetail = {
        ...detail,
        incidence: ManifestIncidence.ERROR,
        errors: insertedDetail.errorMessage,
      };
      inventory(removePendingDetails([invalidDetail]));
      inventory(addProcessedDetails([invalidDetail]));
      return;
    }


    const completeDetail = {
      ...validatedDetail,
      inventoryDetailID: insertedDetail.model[0].inventoryDetailID,
    };

    inventory(removePendingDetails([completeDetail]));
    inventory(addProcessedDetails([completeDetail]));
  };

  const handleBarCodeSubmit = async (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter" && barCode !== "") {
      const shipmentBarCode = processBarcode(barCode);
      if (!shipmentBarCode) {
        alertService.error(
          "El código de barra no pertenece a una enumeración de Tealca."
        );
        return;
      }

      const shipment = await getOfficeShipment(shipmentBarCode.shipmentNumber);
      if (!shipment) {
        alertService.error(
          "shipmentNumber",
          `La guía ${shipmentBarCode.shipmentNumber} no fue encontrada.`
        );
        return;
      }

      const shipmentDetail = shipment.shipmentDetail.find(
        (d) => d.pieceNumber === shipmentBarCode.pieceNumber
      );
      if (!shipmentDetail) {
        alertService.error(
          "pieceNumber",
          `La guía ${shipmentBarCode.shipmentNumber} no tiene una pieza con número ${shipmentBarCode.pieceNumber}.`
        );
        return;
      }

      const service = businessServices.find(
        (s) => s.serviceCode === shipment.serviceCode.toString()
      );
      if (!service) {
        alertService.error(
          "El servicio de la guía asociada a la pieza no está habilitada para esta tienda."
        );
        return;
      }

      // Reload form data
      setInitialValues({
        ...INITIAL_VALUES,
        random: Math.random(),
      });
      setBarCode("");

      const detail: InventoryDetailSingleInterface = {
        statusID: StatusInventory.PROCESS,
        shipmentDetailID: shipmentDetail.shipmentDetailID,
        inventoryDetailID: null,
        shipmentNumber: shipment.shipmentNumber,
        buCodeSource: shipment.buCodeSource,
        serviceCode: shipment.serviceCode.toString(),
        serviceID: service?.serviceID,
        serviceName:
          service?.serviceShortName?.toUpperCase() ||
          service?.serviceDescription?.toUpperCase() ||
          "",
        pieceNumber: shipmentDetail.pieceNumber,
        locationDestinationCode: shipment.locationConsigneeCode,
        totalPieces: shipment.totalPieces,
        manifestType: ManifestType.DESPACHO,
        physicalWeight: shipmentDetail.chargedWeight,
        creationDate: shipment.creationDate ?? "",
        daysCreation: 0,
        difference: 0,
        incidence: ManifestIncidence.NOT_VALIDATED,
        incidenceWarning: false,
        warnings: "",
        errors: "",
        inputType: InputType.SCAN,
        chargedWeight : shipmentDetail.chargedWeight,
      };
      addDetailsBeingValidated([detail]);
      await addDetail(detail);
      removeDetailsBeingValidated([detail]);
    }
  };

  const handleShipmentPieceSubmit = async (
    values: ShipmentPieceFormValues,
    formik: FormikHelpers<ShipmentPieceFormValues>
  ) => {
    const shipment = await getOfficeShipment(values.shipmentNumber);
    if (!shipment) {
      formik.setFieldError(
        "shipmentNumber",
        `La guía ${values.shipmentNumber} no fue encontrada.`
      );
      return;
    }

    const shipmentDetail = shipment.shipmentDetail.find(
      (d) => d.pieceNumber === values.pieceNumber
    );
    if (!shipmentDetail) {
      formik.setFieldError(
        "pieceNumber",
        `La guía ${values.shipmentNumber} no tiene una pieza con número ${values.pieceNumber}.`
      );
      return;
    }

    const service = businessServices.find(
      (s) => s.serviceCode === shipment.serviceCode.toString()
    );
    if (!service) {
      alertService.error(
        "El servicio de la guía asociada a la pieza no está habilitada para esta tienda."
      );
      return;
    }

    formik.resetForm();
    setBarCode("");

    const detail: InventoryDetailSingleInterface = {
      shipmentDetailID: shipmentDetail.shipmentDetailID,
      inventoryDetailID: null,
      shipmentNumber: shipment.shipmentNumber,
      buCodeSource: shipment.buCodeSource,
      serviceCode: shipment.serviceCode.toString(),
      serviceID: service?.serviceID,
      serviceName:
        service?.serviceShortName?.toUpperCase() ||
        service?.serviceDescription?.toUpperCase() ||
        "",
      pieceNumber: shipmentDetail.pieceNumber,
      locationDestinationCode: shipment.locationConsigneeCode,
      totalPieces: shipment.totalPieces,
      manifestType: ManifestType.DESPACHO,
      physicalWeight: shipmentDetail.chargedWeight,
      creationDate: shipment.creationDate ?? "",
      daysCreation: 0,
      difference: 0,
      incidence: ManifestIncidence.NOT_VALIDATED,
      incidenceWarning: false,
      warnings: "",
      errors: "",
      inputType: InputType.MANUAL,
    };
    addDetailsBeingValidated([detail]);
    await addDetail(detail);
    removeDetailsBeingValidated([detail]);
  };

  return (
    <>
      <WarningModal
        openModal={openErrorModal}
        setOpenModal={setOpenErrorModal}
        message={errorMessageModal}
        icon={XCircleIcon}
        showSecondaryButton={false}
        primaryButtonText="Cerrar"
      />
      <div className="flex flex-col sm:flex-row w-full bg-white gap-4 rounded-lg border px-8 pb-6 pt-4">
        <div className="flex-1">
          <FormTextInput
          id="barCode"
          name="barCode"
          label="Código de Barras"
          value={barCode}
          onChange={(e) => setBarCode(e.target.value)}
          onKeyUp={handleBarCodeSubmit}
          placeholder="Escanee el código de barras"
          maxLength={30}
        />
      </div>

      {/* <div className="flex w-[1px] bg-gray-300" />

      <Formik<ShipmentPieceFormValues>
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleShipmentPieceSubmit}
      >
        {(formik) => (
          <form
            onSubmit={formik.handleSubmit}
            className="flex flex-1 flex-row gap-4"
          >
            <div className="flex-[2]">
              <FormTextInput
                id="shipmentNumber"
                name="shipmentNumber"
                label="Número de Guía"
                value={formik.values.shipmentNumber}
                onChange={formik.handleChange}
                error={formik.errors.shipmentNumber}
                placeholder="Introduzca el número de guía"
                maxLength={20}
              />
            </div>

            <div className="flex-1">
              <FormTextInput
                id="pieceNumber"
                name="pieceNumber"
                label="Número de Pieza"
                type="number"
                value={formik.values.pieceNumber}
                onChange={formik.handleChange}
                error={formik.errors.pieceNumber}
                min={0}
                max={999}
                placeholder="Introduzca el número de pieza"
              />
            </div>

            <div className="flex items-end">
              <PrimaryButton>Agregar</PrimaryButton>
            </div>
          </form>
        )}
      </Formik> */}
    </div>
    </>
  );
};

export default InventoryDetailForm;
